import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconArrowLeft, IconPlus, IconUsers } from '@tabler/icons';
import React from 'react';
import { Conversation } from '../../../models/chat';
import { getFullNameInitials } from '../../../utils/get-full-name-initials';
import { useGroupCreation } from '../hooks/useGroupCreation';
import { clickablePaperSx } from '../styles';
import { getHeaderTitle } from '../utils';
import { ConversationItem } from './ConversationItem';
import { ConversationSkeleton } from './Skeletons';
import { UserItem } from './UserItem';

type PanelView = 'list' | 'users' | 'new-group';

interface LeftPaneProps {
  view: PanelView;
  onViewChange: (view: PanelView) => void;
  conversations: Conversation[];
  loadingConversations: boolean;
  users: any[];
  userId?: number;
  onSelectConversation: (id: number) => void;
  onSelectUser: (u: any) => void;
  createGroupConversation: (
    name: string,
    userIds: number[],
  ) => Promise<{ id: number }>;
  fetchConversations: () => Promise<void>;
  onGroupCreated: (conversationId: number) => void;
  isMobile: boolean;
}

export const LeftPane = React.memo(function LeftPane({
  view,
  onViewChange,
  conversations,
  loadingConversations,
  users,
  userId,
  onSelectConversation,
  onSelectUser,
  createGroupConversation,
  fetchConversations,
  onGroupCreated,
  isMobile,
}: LeftPaneProps) {
  const {
    selectedUsersForGroup,
    newGroupName,
    setNewGroupName,
    creatingGroup,
    toggleUserSelection,
    resetForm,
    handleCreateGroup,
  } = useGroupCreation({
    createGroupConversation,
    fetchConversations,
    onSuccess: (id) => {
      onGroupCreated(id);
      onViewChange('list');
    },
  });

  const headerTitle = getHeaderTitle(view);

  return (
    <Box
      sx={{
        width: isMobile ? '100%' : 300,
        display: 'flex',
        flexDirection: 'column',
        borderRight: isMobile ? 'none' : '1px solid #ccc',
      }}
    >
      {/* Header */}
      <Box
        p="md"
        sx={{ borderBottom: '1px solid #ccc', backgroundColor: '#f8f9fa' }}
      >
        <Group position="apart">
          <Group spacing="xs">
            {view !== 'list' && (
              <ActionIcon
                size="sm"
                onClick={() => {
                  onViewChange('list');
                  resetForm();
                }}
              >
                <IconArrowLeft size={16} />
              </ActionIcon>
            )}
            <Text weight={700}>{headerTitle}</Text>
          </Group>
          <Group spacing="xs">
            {view === 'list' && (
              <ActionIcon
                color="blue"
                variant="light"
                onClick={() => onViewChange('users')}
              >
                <IconPlus size={16} />
              </ActionIcon>
            )}
          </Group>
        </Group>
      </Box>

      {/* Content */}
      <ScrollArea sx={{ flex: 1 }} p="md">
        {view === 'list' && (
          <Stack spacing="xs">
            {conversations.length === 0 && loadingConversations ? (
              <ConversationSkeleton />
            ) : (
              <>
                {conversations.map((conv) => (
                  <ConversationItem
                    key={conv.id}
                    conv={conv}
                    userId={userId}
                    onSelect={onSelectConversation}
                  />
                ))}
                {conversations.length === 0 && !loadingConversations && (
                  <Text size="sm" color="dimmed" align="center" mt="xl">
                    Nenhuma conversa encontrada
                  </Text>
                )}
              </>
            )}
          </Stack>
        )}

        {view === 'users' && (
          <Stack spacing="xs">
            <Paper
              withBorder
              p="xs"
              sx={clickablePaperSx}
              onClick={() => {
                onViewChange('new-group');
                resetForm();
              }}
            >
              <Group spacing="xs">
                <Avatar size="sm" radius="xl" color="cyan">
                  <IconUsers size={18} />
                </Avatar>
                <Text weight={500} size="sm">
                  Criar Novo Grupo
                </Text>
              </Group>
            </Paper>
            <Divider my="xs" />
            {users.map((u) => (
              <UserItem key={u.id} u={u} onSelect={onSelectUser} />
            ))}
          </Stack>
        )}

        {view === 'new-group' && (
          <Stack spacing="md">
            <TextInput
              placeholder="Nome do Grupo"
              label="Nome do Grupo"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.currentTarget.value)}
              required
            />
            <Text size="sm" weight={500}>
              Participantes
            </Text>
            <Stack spacing="xs">
              {users.map((u) => {
                const isSelected = selectedUsersForGroup.includes(u.id);
                return (
                  <Paper
                    key={u.id}
                    withBorder
                    p="xs"
                    sx={(t) => ({
                      ...clickablePaperSx,
                      borderColor: isSelected ? t.colors.blue[5] : undefined,
                      backgroundColor: isSelected
                        ? t.colors.blue[0]
                        : undefined,
                    })}
                    onClick={() => toggleUserSelection(u.id)}
                  >
                    <Group position="apart">
                      <Group spacing="xs">
                        <Avatar
                          src={u.avatarUrl}
                          size="sm"
                          radius="xl"
                          color="blue"
                        >
                          {!u.avatarUrl &&
                            getFullNameInitials(u.name || u.username)}
                        </Avatar>
                        <Text weight={500} size="sm">
                          {u.name}
                        </Text>
                      </Group>
                      <Checkbox checked={isSelected} readOnly />
                    </Group>
                  </Paper>
                );
              })}
            </Stack>
            <Button
              loading={creatingGroup}
              disabled={
                !newGroupName.trim() || selectedUsersForGroup.length === 0
              }
              onClick={handleCreateGroup}
            >
              Criar Grupo
            </Button>
          </Stack>
        )}
      </ScrollArea>
    </Box>
  );
});
